<template>
  <div id="master-container">
    <b-container fluid class="max-width">
      <title-back-button
        :title="isAugmented ? trans('courses.new_dashboard.augmented_title') : courseName"
        :show-breadcrumbs="breadcrumb && breadcrumb.hasOwnProperty('backLink')"
        :current-page="breadcrumb ? breadcrumb.currentPage : null"
        :prev-page="breadcrumb ? breadcrumb.prevPage : null"
        :prev-page-link="breadcrumb ? breadcrumb.backLink : null"
      />
      <div class="row">
        <div class="col-10 offset-lg-1 mb-2">
          <h4>{{ isAugmented ? webinarTitle : this.trans("courses.new_dashboard.your_path") }}</h4>
          <p v-html="courseDescription" class="mb-4" />
        </div>
        <div class="col-md-5" v-if="masterImage">
          <div class="text-center">
            <b-img :src="masterImage" fluid class="master-image" />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4 col-lg-3 offset-lg-1 pb-md-5">
          <b-toast
            id="locked-chapter-toast"
            :title="trans('courses.new_dashboard.blocked')"
            auto-hide-delay="2000"
            class="blocked-toast"
          >
            {{ trans("courses.new_dashboard.blocked_description") }}
          </b-toast>
          <progress-state
            v-if="canAccessCertificate"
            :cardTitle="trans('courses.new_dashboard.mission_completed')"
            :buttonTitle="trans('courses.new_dashboard.see_certificate')"
            :title="trans('courses.new_dashboard.certificate')"
            :subTitle="`${courseName} certificate`"
            @ctaClick="$router.push(`/programs/${courseId}/certificate`)"
          />
          <progress-state
            v-else-if="showWhatsNextCard"
            :cardTitle="trans('courses.new_dashboard.what_next')"
            :buttonTitle="trans('courses.new_dashboard.continue_course')"
            :title="progressData.currentItem.moduleName"
            :subTitle="progressData.currentItem.chapterName"
            @ctaClick="$router.push(parseMasterNav(progressData.currentItem.route))"
          />
          <progress-state
            v-if="onlyMissionsLeft && progressData.missions.scheduledRoutes.length > 0"
            :buttonLink="`/mission/${progressData.missions.scheduledRoutes[0].object_id}/complete`"
            :cardTitle="trans('courses.new_dashboard.have_to_complete_mission')"
            :buttonTitle="trans('courses.new_dashboard.complete_mission')"
            :title="progressData.missions.parentModuleName"
            :subTitle="progressData.missions.missionName"
          />
          <user-progress
            v-if="!isMobile"
            :allModules="progressData.allMandatoryModules"
            :completedModules="progressData.completedModules"
            :displayValue="`${progress}%`"
            :progressLabel="progressLabel"
            :progress="progress"
          >
            <chapters-progress
              :title="trans('courses.new_dashboard.missions')"
              :completed="progressData.missions.completed"
              :total="progressData.missions.count"
              :infoText="
                trans('courses.new_dashboard.have_completed_missions', {
                  completedMissions_num: progressData.missions.completed,
                  totalMissions_num: progressData.missions.count,
                })
              "
              type="star"
            />
            <chapters-progress
              :title="trans('courses.new_dashboard.soft_skills')"
              :completed="softSkillsCompleted"
              :total="softSkillsTotal"
              :infoText="
                trans('courses.new_dashboard.have_completed_skills', {
                  completedSkills_num: softSkillsCompleted,
                  totalSkills_num: softSkillsTotal,
                })
              "
              type="trophy"
            />
          </user-progress>
        </div>
        <div class="col-md-8 col-lg-7 pb-md-5">
          <div class="row course-section">
            <course-section
              v-for="section in sections"
              :isAugmented="isAugmented"
              :key="section.id"
              :section="section"
              :answerFork="answerFork"
              :answerShortcut="answerShortcut"
              @loading="loading"
            />
          </div>
        </div>
      </div>

      <div class="row" v-if="isMobile">
        <div class="col-12 pb-5 pt-5">
          <user-progress
            :allModules="progressData.allMandatoryModules"
            :completedModules="progressData.completedModules"
            :displayValue="`${progress}%`"
            :progressLabel="progressLabel"
            :progress="progress"
          >
            <chapters-progress
              :title="trans('courses.new_dashboard.missions')"
              :completed="progressData.missions.completed"
              :total="progressData.missions.count"
              :infoText="
                trans('courses.new_dashboard.have_completed_missions', {
                  completedMissions_num: progressData.missions.completed,
                  totalMissions_num: progressData.missions.count,
                })
              "
              type="star"
            />
            <chapters-progress
              :title="trans('courses.new_dashboard.soft_skills')"
              :completed="softSkillsCompleted"
              :total="softSkillsTotal"
              :infoText="
                trans('courses.new_dashboard.have_completed_skills', {
                  completedSkills_num: softSkillsCompleted,
                  totalSkills_num: softSkillsTotal,
                })
              "
              type="trophy"
            />
          </user-progress>
        </div>
      </div>

      <div class="loading" v-if="isLoading">
        <div class="justify-content-center align-items-center spinnerContainer">
          <b-spinner variant="secondary" type="grow" label="Spinning"></b-spinner>
        </div>
      </div>
    </b-container>
  </div>
</template>

<script>
import CourseSection from "../organisms/CourseSection";
import ProgressState from "../molecules/ProgressState";
import UserProgress from "../molecules/UserProgress";
import ChaptersProgress from "../molecules/ChaptersProgress";
import TitleBackButton from "../molecules/TitleBackButton";
import navMixin from "../mixins/navMixin";

export default {
  name: "MasterDashboard",
  components: { CourseSection, ProgressState, UserProgress, TitleBackButton, ChaptersProgress },
  mixins: [navMixin],
  props: {
    breadcrumb: Object,
    webinarId: String,
    masterImage: String,
  },
  data() {
    return {
      courseId: 0,
      sections: [],
      isLoading: false,
      canAccessCertificate: false,
      assessmentEnabled: 0,
      courseName: "",
      courseDescription: "",
      webinarTitle: null,
      screenWidth: 0,
      progress: 0,
      softSkillsTotal: 0,
      softSkillsCompleted: 0,
    };
  },
  mounted() {
    this.getCourse();
  },
  methods: {
    getCourse() {
      this.loading(true);
      this.$axios
        .get(
          `/api/rest/programs/${
            this.webinarId ? "webinars/" + this.webinarId : this.$route.params.id
          }`
        )
        .then((res) => {
          const master = res.data.master;
          this.courseId = master.courseId;
          this.sections = master.sections;
          this.certificate = master.certificate;
          this.canAccessCertificate = master.canAccessCertificate;
          this.courseName = master.courseName;
          this.courseDescription = master.courseDesc;
          this.webinarTitle = master.webinarName;
          this.progress = master.progress.percentage;
          this.softSkillsTotal = master.progress.totalSkills;
          this.softSkillsCompleted = master.progress.completedSkills;
          trackEvent("MasterDashboardView", {
            courseId: this.courseId,
          });
        })
        .catch((e) => {
          console.log(e);
        })
        .finally(() => this.loading(false));
    },
    answerFork() {
      this.getCourse();
    },
    answerShortcut(phaseId) {
      this.$axios
        .post("/api/rest/programs/user_shortcut/save", {
          phase_id: phaseId,
        })
        .then(() => {
          this.getCourse();
        });
    },
    loading(state) {
      this.isLoading = state;
    },
    handleResize() {
      this.screenWidth = window.innerWidth;
    },
  },
  computed: {
    showWhatsNextCard() {
      return (
        (this.progressData.completedModules !== 0 || this.softSkillsCompleted !== 0) &&
        this.progressData.currentItem.chapterName !== ""
      );
    },
    isAugmented() {
      return this.webinarId !== undefined;
    },
    onlyMissionsLeft() {
      if (
        this.softSkillsTotal === this.softSkillsCompleted &&
        this.progressData.missions.count !== this.progressData.missions.completed
      ) {
        return true;
      }
    },
    progressLabel() {
      if (this.progress === 0) {
        return this.trans("courses.new_dashboard.starting_point");
      } else if (this.progress > 0 && this.progress < 75) {
        return this.trans("courses.new_dashboard.keep_going");
      } else if (this.progress >= 75 && this.progress < 100) {
        return this.trans("courses.new_dashboard.few_steps_away");
      } else if (this.progress === 100) {
        return this.trans("courses.new_dashboard.well_done");
      }
    },
    progressData: function () {
      let progressData = {
        completedModules: 0,
        scheduledMissions: 0,
        allMandatoryModules: 0,
        missions: {
          completed: 0,
          count: 0,
          scheduledRoutes: [],
          missionName: "",
          parentModuleName: "",
        },
        currentItem: {
          moduleName: "",
          chapterName: "",
          route: "",
        },
      };
      const selectModules = Object.entries(this.sections);
      selectModules.map((module) =>
        module[1].modules.map((module) => {
          switch (module.state) {
            case "current": {
              progressData.currentItem.moduleName = module.name;
              break;
            }
            case "completed": {
              if (module.isMandatory === true) {
                progressData.completedModules++;
              }
              break;
            }
            case "waiting":
              {
                progressData.scheduledMissions++;
                break;
              }
              return;
          }
          if (module.isMandatory === true) {
            progressData.allMandatoryModules++;
          }

          module.chapters.map((chapter) => {
            if (chapter.state === "current") {
              progressData.currentItem.chapterName = chapter.name;
              progressData.currentItem.route = chapter.route;
              return;
            }
            if (chapter.type === "star") {
              progressData.missions.count++;
              if (chapter.state === "completed") {
                progressData.missions.completed++;
              }
              if (
                chapter.missionStatus === "scheduled" &&
                progressData.missions.scheduledRoutes.length === 0
              ) {
                progressData.missions.scheduledRoutes.push(chapter.route);
                progressData.missions.missionName = chapter.name;
                progressData.missions.parentModuleName = module.name;
              }
            }
          });
        })
      );
      return progressData;
    },
    isMobile() {
      return this.screenWidth > 768 ? false : true;
    },
  },
  created() {
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  },
  destroyed() {
    window.removeEventListener("resize", this.handleResize);
  },
  watch: {
    $route(to, from) {
      this.getCourse();
    },
  },
};
</script>

<style scoped lang="scss">
.loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 120%;
  background-color: $color-white;
  opacity: 0.5;
  z-index: 99999999999;
}
.spinnerContainer {
  display: flex;
  width: 100%;
  height: 100%;
}

.b-toast {
  top: 72px;
}
::v-deep .course-section {
  > div:first-child {
    > div:first-child {
      .row.pt-4 {
        padding-top: 0 !important;
      }
    }
  }
}
</style>
